import * as React from "react";

import { DialogHeader, DialogTitle, DialogDescription } from "../dialog";
import { Icon } from "../icon";
import { cn } from "../utils";

/**
 * ModalHeader — the ONE way to title a modal (Dialog), the surface-level
 * counterpart to `SectionTitle` (which titles a Card/section) and `PageHeader`
 * (which titles a page).
 *
 * The bare `DialogTitle` atom renders at `type-body leading-none` — deliberately
 * tiny so a confirm/alert dialog stays quiet — which means a content dialog
 * (the "Add to your corpus" capture surface) reads with no title at all: the
 * heading is the same size as its own description. This block gives a modal a
 * REAL title on the design-language type ramp while keeping the Radix
 * `Dialog.Title`/`Dialog.Description` primitives underneath (so `aria-labelledby`
 * / `aria-describedby` stay wired — a dialog with no Title trips Radix's a11y
 * warning).
 *
 * Type ramp mirrors `SectionTitle` (`lg` → `type-title font-medium`): a modal title
 * sits just under a page title, above a section title. The optional `icon` chip
 * echoes the intake overlay's rounded glyph so a capture/upload modal reads as
 * an action surface, not a form.
 */
export interface ModalHeaderProps {
  /** The modal title — the Radix `Dialog.Title`. */
  title: React.ReactNode;
  /**
   * One muted line under the title — the Radix `Dialog.Description`. Strongly
   * recommended (a `DialogContent` with no description warns in Radix); pass
   * `false`/omit only when the body itself is self-describing.
   */
  description?: React.ReactNode;
  /** Tiny mono kicker above the title (rare — e.g. a step marker or scope). */
  eyebrow?: React.ReactNode;
  /**
   * Optional leading glyph (kebab name, resolved through `<Icon>`), rendered in
   * a soft rounded chip beside the title — the intake-overlay affordance.
   */
  icon?: string;
  /** Extra classes on the header wrapper. */
  className?: string;
}

export function ModalHeader({
  title,
  description,
  eyebrow,
  icon,
  className,
}: ModalHeaderProps) {
  return (
    // Left-aligned always (the atom centers on mobile); the title row carries
    // the optional icon chip, the description spans full width beneath it.
    <DialogHeader className={cn("gap-1.5 text-left", className)}>
      {eyebrow != null ? (
        <span className="type-subtext font-medium uppercase tracking-[0.14em] text-muted-foreground">
          {eyebrow}
        </span>
      ) : null}
      <div className="flex items-center gap-2.5">
        {icon != null ? (
          <span className="grid size-8 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary">
            <Icon name={icon} size="base" />
          </span>
        ) : null}
        <DialogTitle className="type-title font-medium tracking-tight text-foreground">
          {title}
        </DialogTitle>
      </div>
      {description ? (
        <DialogDescription className="type-body leading-body text-muted-foreground">
          {description}
        </DialogDescription>
      ) : null}
    </DialogHeader>
  );
}
